import { Card, Divider, ScrollArea, Stack, Text } from "@mantine/core";
import UserProfileCard from "components/UserProfileCard";
import { useNavigate } from "react-router-dom";

export interface TeamPlayerListProps {
  /** Players in the team; use minimal shape required by the compact/inline card */
  players: Array<{
    id: string;
    name: string;
    avatarUrl?: string;
    team?: string;
    franchise?: string;
    roles?: string[];
  }>;
  /**
   * Optional max height for the list container. If provided, the list will scroll
   * within this height and never push other layout rows down.
   */
  maxHeight?: number | string;
}

/**
 * TeamPlayerList
 *
 * Renders a vertical list of players using the `UserProfileCard` in "inline" variant
 * for an ultra-compact row. Each row is clickable; for now, it logs to the console.
 * Replace the handler with navigation to the player's profile once routes exist.
 */
export default function TeamPlayerList({ players, maxHeight }: TeamPlayerListProps) {
  const navigate = useNavigate();
  return (
    <Card withBorder radius="md" p="md" style={{ height: "100%", width: '100%' }}>
      {/* Fill the available height so the internal ScrollArea can use 100% */}
      <Stack gap="sm" style={{ height: "100%" }}>
        <Text fw={700}>Roster</Text>
        <ScrollArea.Autosize style={{ flex: 1, minHeight: 0 }} mah={maxHeight ?? "100%"}>
          {/* Use a subtle divider between inline rows for a tight list */}
          <Stack gap={8}>
            {players.map((p, idx) => (
              <div key={p.id}>
                <div
                  onClick={() => {
                    navigate(`/users/${p.id}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <UserProfileCard
                    variant="inline"
                    avatarUrl={p.avatarUrl}
                    name={p.name}
                    team={p.team}
                    franchise={p.franchise}
                    roles={p.roles}
                    // In roster context we already know the team/franchise; hide these badges for tighter rows.
                    displayOptions={{ showTeam: false, showFranchise: false }}
                  />
                </div>
                {idx < players.length - 1 && <Divider variant="dashed" />}
              </div>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Card>
  );
}
